import api from "@/lib/api";
import {
  Game,
  CreateGameData,
  UpdateGameData,
  GetGamesFilter,
  MyParticipationsEnrichedResponse,
  FindUserParticipatedGamesEnrichedParams,
} from "@/types/game";
import type {
  GameGalleryPhoto,
  GameGalleryResponse,
  UserGameGalleriesResponse,
  FindUserGameGalleriesParams,
  ViplayArenasResponse,
  ViplaySyncConfig,
  ViplaySyncResult,
  SyncViplayGalleryPayload,
} from "@/types/game-gallery";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const GAMES_EDGE_URL =
  SUPABASE_URL && import.meta.env.PROD ? `${SUPABASE_URL}/functions/v1` : null;

const EDGE_TIMEOUT_MS = 2500;

async function fetchWithFallback<T>(
  url: string,
  fallback: () => Promise<T>,
  init?: RequestInit,
): Promise<T> {
  if (!GAMES_EDGE_URL) return fallback();

  const apiPromise = fallback();

  const edgePromise = (async (): Promise<T> => {
    try {
      const res = await fetch(url, {
        ...init,
        signal: AbortSignal.timeout(EDGE_TIMEOUT_MS),
      });
      const data = await res.json();
      if (res.status === 502 || res.status === 503 || data?.fallback) {
        return apiPromise;
      }
      if (!res.ok) {
        throw new Error((data as { message?: string })?.message ?? `HTTP ${res.status}`);
      }
      return data as T;
    } catch {
      return apiPromise;
    }
  })();

  return Promise.race([edgePromise, apiPromise]);
}

export type GameFeedbackEntry = {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string | null;
  stars: number;
  comment: string | null;
  isPublic?: boolean;
  likesCount: number;
  dislikesCount: number;
  myReaction: "LIKE" | "DISLIKE" | null;
  replies: Array<{
    id: string;
    userId: string;
    userName: string;
    userAvatar: string | null;
    comment: string;
    createdAt: string;
  }>;
  updatedAt: string;
};

export type GameFeedbackPayload = {
  canSubmitFeedback: boolean;
  myFeedback: {
    stars: number;
    comment: string | null;
    isPublic: boolean;
    updatedAt: string;
  } | null;
  summary: {
    averageStars: number;
    feedbackCount: number;
  };
  feedbacks: GameFeedbackEntry[];
};

export type GamePublicFeedbackPayload = {
  summary: {
    averageStars: number;
    feedbackCount: number;
  };
  publicFeedbackCount: number;
  feedbacks: Array<{
    id: string;
    userId: string;
    userName: string;
    userAvatar: string | null;
    stars: number;
    comment: string | null;
    updatedAt: string;
  }>;
};

export class GameService {
  async getFeedback(gameId: string): Promise<GameFeedbackPayload> {
    const response = await api.get(`/games/${gameId}/feedback`);
    return response.data;
  }

  async getPublicFeedback(gameId: string): Promise<GamePublicFeedbackPayload> {
    const response = await api.get(`/games/${gameId}/feedback/public`);
    return response.data;
  }

  async submitFeedback(
    gameId: string,
    payload: { stars: number; comment?: string; isPublic?: boolean },
  ) {
    const response = await api.post(`/games/${gameId}/feedback`, payload);
    return response.data;
  }

  async toggleFeedbackLike(gameId: string, feedbackId: string) {
    const response = await api.post(`/games/${gameId}/feedback/${feedbackId}/like`);
    return response.data;
  }

  async setFeedbackReaction(gameId: string, feedbackId: string, reaction: "LIKE" | "DISLIKE") {
    const response = await api.post(`/games/${gameId}/feedback/${feedbackId}/reaction`, { reaction });
    return response.data;
  }

  async replyFeedback(gameId: string, feedbackId: string, comment: string) {
    const response = await api.post(`/games/${gameId}/feedback/${feedbackId}/replies`, { comment });
    return response.data;
  }

  async deleteFeedback(gameId: string, feedbackId: string) {
    const response = await api.delete(`/games/${gameId}/feedback/${feedbackId}`);
    return response.data;
  }

  async getGalleryPhotos(gameId: string): Promise<GameGalleryResponse> {
    const response = await api.get(`/games/${gameId}/gallery-photos`);
    return response.data;
  }

  async findUserGameGalleries(
    params?: FindUserGameGalleriesParams,
  ): Promise<UserGameGalleriesResponse> {
    const response = await api.get<UserGameGalleriesResponse>("/games/my-galleries", {
      params: {
        page: params?.page,
        limit: params?.limit,
      },
    });
    return response.data;
  }

  async uploadGalleryPhoto(gameId: string, file: File): Promise<GameGalleryPhoto> {
    const formData = new FormData();
    formData.append("photo", file);
    const response = await api.post(`/games/${gameId}/gallery-photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async toggleGalleryPhotoLike(
    gameId: string,
    photoId: string,
  ): Promise<{ likedByMe: boolean; likesCount: number }> {
    const response = await api.post(`/games/${gameId}/gallery-photos/${photoId}/like`);
    return response.data;
  }

  async deleteGalleryPhoto(gameId: string, photoId: string): Promise<void> {
    await api.delete(`/games/${gameId}/gallery-photos/${photoId}`);
  }

  async setCoverFromGalleryPhoto(gameId: string, photoId: string): Promise<Game> {
    const response = await api.post<Game>(`/games/${gameId}/gallery-photos/${photoId}/set-cover`);
    return response.data;
  }

  async getViplayArenas(): Promise<ViplayArenasResponse> {
    const response = await api.get<ViplayArenasResponse>("/games/viplay/arenas");
    return response.data;
  }

  async getViplaySyncConfig(gameId: string): Promise<ViplaySyncConfig> {
    const response = await api.get<ViplaySyncConfig>(`/games/${gameId}/viplay/sync-config`);
    return response.data;
  }

  async syncViplayGallery(gameId: string, payload: SyncViplayGalleryPayload): Promise<ViplaySyncResult> {
    const response = await api.post<ViplaySyncResult>(`/games/${gameId}/viplay/sync`, payload);
    return response.data;
  }

  async create(data: CreateGameData): Promise<Game> {
    const response = await api.post<Game>("/games", data);
    return response.data;
  }

  async findAll(): Promise<Game[]> {
    const response = await api.get<Game[]>("/games");
    return response.data;
  }

  async findPublic(filters?: GetGamesFilter): Promise<Game[]> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.sport) params.append("sport", filters.sport);
      if (filters.status) params.append("status", filters.status);
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);
      if (filters.includeFinishedStatuses)
        params.append("includeFinishedStatuses", filters.includeFinishedStatuses);
      if (filters.search) params.append("search", filters.search);
      if (filters.latitude) params.append("latitude", filters.latitude);
      if (filters.longitude) params.append("longitude", filters.longitude);
      if (filters.distance) params.append("distance", filters.distance);
    }
    const query = params.toString();
    const edgeUrl = `${GAMES_EDGE_URL}/games-public${query ? `?${query}` : ""}`;
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("accessToken") : null;
    const init: RequestInit = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    return fetchWithFallback(
      edgeUrl,
      () => api.get<Game[]>(`/games/public?${query}`).then(({ data }) => data),
      init,
    );
  }

  async findUserParticipatedGames(): Promise<Game[]> {
    const response = await api.get<Game[]>("/games/my-participations");
    return response.data;
  }

  async findCalendarGames(
    start: string,
    end: string,
  ): Promise<
    (Game & {
      userParticipantStatus?: string;
      isOrganizer?: boolean;
    })[]
  > {
    const params = new URLSearchParams({ start, end });
    const response = await api.get(`/games/calendar?${params.toString()}`);
    return response.data;
  }

  async findUserParticipatedGamesEnriched(
    params?: FindUserParticipatedGamesEnrichedParams,
  ): Promise<MyParticipationsEnrichedResponse> {
    const response = await api.get<MyParticipationsEnrichedResponse>(
      "/games/my-participations-enriched",
      {
        params: {
          scope: params?.scope,
          page: params?.page,
          limit: params?.limit,
        },
      },
    );
    return response.data;
  }

  async findOne(id: string): Promise<Game> {
    const edgeUrl = `${GAMES_EDGE_URL}/games-detail?id=${encodeURIComponent(id)}`;
    return fetchWithFallback(edgeUrl, () =>
      api.get<Game>(`/games/${id}`).then(({ data }) => data),
    );
  }

  async findByShortCode(shortCode: string): Promise<Game> {
    const response = await api.get<Game>(`/games/by-short/${shortCode}`);
    return response.data;
  }

  async update(id: string, data: UpdateGameData): Promise<Game> {
    const response = await api.put<Game>(`/games/${id}`, data);
    return response.data;
  }

  async uploadCoverImage(gameId: string, file: File): Promise<Game> {
    const formData = new FormData();
    formData.append("coverImage", file);
    const response = await api.post<Game>(`/games/${gameId}/cover-image`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async removeCoverImage(gameId: string): Promise<Game> {
    const response = await api.delete<Game>(`/games/${gameId}/cover-image`);
    return response.data;
  }

  async uploadAttachment(gameId: string, file: File, label?: string): Promise<Game> {
    const formData = new FormData();
    formData.append("file", file);
    if (label?.trim()) formData.append("label", label.trim());
    const response = await api.post<Game>(`/games/${gameId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async removeAttachment(gameId: string, attachmentId: string): Promise<Game> {
    const response = await api.delete<Game>(`/games/${gameId}/attachments/${attachmentId}`);
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/games/${id}`);
  }

  async registerParticipant(
    id: string,
    paymentData?: {
      paymentMethod?: string;
      price?: number;
      fee?: number;
      total?: number;
      /** Fluxo Panelinha: ID do usuário que receberá a vaga (quem paga é o usuário logado) */
      participantUserId?: string;
      preferredPosition?: string | null;
    },
  ): Promise<{ verificationCode: string; participantId: string }> {
    const response = await api.post<{
      verificationCode: string;
      participantId: string;
    }>(`/games/${id}/register`, paymentData || {});
    return response.data;
  }

  /** Registra múltiplos participantes de uma vez (panelinha). Retorna IDs dos GameParticipants. */
  async registerParticipantsBulk(
    id: string,
    params: {
      participantUserIds: string[];
      paymentMethod?: string;
      price?: number;
      preferredPosition?: string | null;
    },
  ): Promise<{ participantIds: string[] }> {
    const response = await api.post<{ participantIds: string[] }>(`/games/${id}/register-bulk`, params);
    return response.data;
  }

  /**
   * Garante inscrição (se necessário) e persiste a posição escolhida no checkout.
   * Idempotente para quem já está PENDING_PAYMENT ou CONFIRMED.
   */
  async syncPreferredPositionAtCheckout(
    gameId: string,
    preferredPosition: string | null | undefined,
    paymentData?: {
      paymentMethod?: string;
      price?: number;
    },
  ): Promise<void> {
    const normalized =
      preferredPosition != null && preferredPosition.trim() !== ""
        ? preferredPosition.trim()
        : null;
    const hasPaymentData =
      paymentData?.paymentMethod != null || paymentData?.price != null;
    if (!normalized && !hasPaymentData) return;

    try {
      await this.registerParticipant(gameId, {
        ...paymentData,
        preferredPosition: normalized,
      });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "";
      if (String(message).toLowerCase().includes("já está registrado")) {
        if (normalized) {
          await this.updateMyPreferredPosition(gameId, normalized);
        }
        return;
      }
      throw error;
    }
  }

  async verifyPayment(verificationCode: string): Promise<{
    valid: boolean;
    game: {
      id: string;
      name: string;
      gameDate: string;
      gameEndDate?: string | null;
      location: string;
      team?: {
        name: string;
        photo?: string;
      };
    };
    participant: {
      name: string;
      paymentMethod?: string;
      price?: number;
      discount?: number;
      fee?: number;
      total?: number;
      registeredAt: string;
      checkedInAt?: string | null;
      status?: string;
    };
    paidBy?: { id: string; name: string | null } | null;
  }> {
    const response = await api.get(`/games/verify-payment/${verificationCode}`);
    return response.data;
  }

  async getCheckInContext(gameId: string): Promise<{
    participant: {
      name: string;
      status: string;
      checkedInAt: string | null;
    };
    game: {
      id: string;
      name: string;
      sport: string;
      gameDate: string;
      gameEndDate: string | null;
      location: string;
      locationLat: number | null;
      locationLng: number | null;
      team: { name: string; photo: string | null } | null;
    };
    squad: {
      teamNumber: number;
      teammates: Array<{ name: string; avatar: string | null }>;
    } | null;
  }> {
    const response = await api.get(`/games/${gameId}/check-in-context`);
    return response.data;
  }

  async checkIn(
    gameId: string,
    verificationCode: string,
  ): Promise<{
    success: boolean;
    message: string;
    participant: {
      name: string;
      email: string;
      checkedInAt: string;
    };
  }> {
    const response = await api.post(`/games/${gameId}/check-in`, {
      verificationCode,
    });
    return response.data;
  }

  async selfCheckIn(
    gameId: string,
    latitude: number,
    longitude: number,
  ): Promise<{
    success: boolean;
    message: string;
    checkedInAt: string;
    checkInPoints: number;
    earlyBonus: boolean;
  }> {
    const response = await api.post(`/games/${gameId}/self-check-in`, {
      latitude,
      longitude,
    });
    return response.data;
  }

  async getParticipantsDetails(gameId: string): Promise<{
    game: {
      id: string;
      name: string;
      gameDate: string;
      location: string;
      pricePerPlayer: number;
      allowRefund: boolean;
      refundDeadlineHours: number;
      refundDeadline: string;
    };
    transactions: Array<{
      id: string;
      paymentId?: string;
      participantId?: string;
      providerPaymentId: string | null;
      providerRefundId: string | null;
      status: string;
      method: string;
      amount: number;
      fee: number | null;
      netAmount: number | null;
      createdAt: string;
      paidAt: string | null;
      refundedAt: string | null;
      canceledAt: string | null;
      userName: string;
      userEmail: string | null;
      participantName?: string;
      paidByName?: string;
      paidByEmail?: string | null;
      cancelledNoRefund?: boolean;
      canRefund?: boolean;
      slotTransferred?: boolean;
      slotTransferredAt?: string | null;
      slotTransferredFromUserId?: string | null;
      slotTransferredToUserId?: string | null;
    }>;
    participants: Array<{
      id: string;
      userId: string;
      userName: string;
      userEmail?: string;
      userPhone?: string;
      status: string;
      paymentMethod?: string | null;
      price?: number | null;
      discount?: number | null;
      amountDue?: number | null;
      listOrder?: number | null;
      registeredAt: string;
      paymentExpiresAt?: string | null;
      paidAt?: string | null;
      paymentId?: string | null;
      providerPaymentId?: string | null;
      amount?: number | null;
      canRefund: boolean;
      refundDeadline: string;
      refundedAt?: string | null;
      providerRefundId?: string | null;
      checkedInAt?: string | null;
    }>;
  }> {
    const response = await api.get(`/games/${gameId}/participants/details`);
    return response.data;
  }

  async getParticipants(id: string): Promise<{
    confirmed: Array<{
      id: string;
      userId?: string;
      name: string;
      email?: string;
      avatar?: string;
      status: string;
      joinedAt: string;
      listOrder?: number | null;
      preferredPosition?: string | null;
      teamRole?: string | null;
      price?: string | number | null;
      discount?: string | number | null;
      amountDue?: string | number | null;
      paymentMethod?: string | null;
      paymentExpiresAt?: string | null;
      checkedInAt?: string | null;
    }>;
    waiting: Array<{
      id: string;
      userId?: string;
      name: string;
      email?: string;
      avatar?: string;
      status: string;
      joinedAt: string;
      listOrder?: number | null;
      preferredPosition?: string | null;
      teamRole?: string | null;
      price?: string | number | null;
      discount?: string | number | null;
      amountDue?: string | number | null;
      paymentMethod?: string | null;
      paymentExpiresAt?: string | null;
      checkedInAt?: string | null;
    }>;
    total: number;
    confirmedCount: number;
    waitingCount: number;
  }> {
    const response = await api.get(`/games/${id}/participants`);
    return response.data;
  }

  async isUserRegistered(gameId: string): Promise<boolean> {
    const response = await api.get<{ isRegistered: boolean }>(
      `/games/${gameId}/is-registered`,
    );
    return response.data.isRegistered;
  }

  /**
   * Status de participação em vários jogos de uma vez (evita N+1 no dashboard).
   */
  async getParticipantStatusBatch(
    gameIds: string[],
  ): Promise<Record<string, { status: string; paymentExpiresAt: string | null; preferredPosition: string | null; checkedInAt: string | null } | null>> {
    if (gameIds.length === 0) return {};
    const response = await api.get<
      Record<string, { status: string; paymentExpiresAt: string | null; preferredPosition: string | null; checkedInAt: string | null } | null>
    >(
      `/games/participant-status?gameIds=${gameIds.join(',')}`,
    );
    return response.data;
  }

  async updateMyPreferredPosition(
    gameId: string,
    preferredPosition: string | null,
  ): Promise<{ preferredPosition: string | null }> {
    const response = await api.patch<{ preferredPosition: string | null }>(
      `/games/${gameId}/my-preferred-position`,
      { preferredPosition },
    );
    return response.data;
  }

  /** Admin: atualizar posição pretendida de um participante */
  async updateParticipantPreferredPositionByAdmin(
    gameId: string,
    userId: string,
    preferredPosition: string | null,
  ): Promise<{ userId: string; preferredPosition: string | null }> {
    const response = await api.patch<{ userId: string; preferredPosition: string | null }>(
      `/games/${gameId}/participants/preferred-position`,
      { userId, preferredPosition },
    );
    return response.data;
  }

  async getUserVerificationCode(gameId: string): Promise<string> {
    const response = await api.get<{ verificationCode: string }>(
      `/games/${gameId}/verification-code`,
    );
    return response.data.verificationCode;
  }

  async cancelParticipant(gameId: string): Promise<void> {
    await api.delete(`/games/${gameId}/register`);
  }

  /** Admin: cancelar participante (criador do jogo ou dono/admin do time) */
  async cancelParticipantByAdmin(
    gameId: string,
    targetUserId: string,
  ): Promise<void> {
    await api.post(`/games/${gameId}/participants/cancel`, {
      userId: targetUserId,
    });
  }

  /** Admin: repassar vaga de um participante para outro (criador do jogo ou dono/admin do time). */
  async transferParticipantSlot(
    gameId: string,
    fromUserId: string,
    toUserId: string,
  ): Promise<void> {
    await api.post(`/games/${gameId}/participants/transfer`, {
      fromUserId,
      toUserId,
    });
  }

  /** Admin: reembolsar transação (criador do jogo ou dono/admin do time).
   * Para pagamentos panelinha (múltiplos participantes), passa gameParticipantId para reembolso parcial. */
  async refundTransaction(
    gameId: string,
    paymentId: string,
    body?: { gameParticipantId?: string },
  ): Promise<void> {
    await api.post(`/games/${gameId}/transactions/${paymentId}/refund`, body ?? {});
  }

  async confirmPayment(gameId: string): Promise<void> {
    await api.post(`/games/${gameId}/confirm-payment`);
  }

  /** Admin/dono: organizar lista de interesse após revelação (WAITING_REVEAL → vagas). */
  async organizeInterestList(gameId: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      `/games/${gameId}/organize-interest-list`,
    );
    return response.data;
  }

  /** [DEV] Inscrever até N membros do time (manifestação ou pós-revelação). */
  async devSeedInterestMembers(
    gameId: string,
    body?: { maxToAdd?: number },
  ): Promise<{ added: number; userIds: string[] }> {
    const response = await api.post<{ added: number; userIds: string[] }>(
      `/games/${gameId}/dev/seed-interest-members`,
      body ?? {},
    );
    return response.data;
  }
}

export const gameService = new GameService();
